// src/components/ConsultationVoicePanel.tsx
// Doctor-patient conversation recorder for the Consultation stage. Manual
// start/stop only (no ambient/continuous listening) — the doctor controls
// exactly what gets recorded. Flow: record (or upload a recorded file) →
// transcribe → review/edit → translate to English → mask PII locally
// (regex-only, no AI call for this step) → hand the reviewed, masked text back
// to the chat composer so the doctor can still edit it before it's ever sent
// to the AI provider.
//
// Recordings of any length are supported — useSegmentedRecorder rolls the
// capture into <=25s segments (the speech service rejects clips over 30s), and
// an uploaded file is cut into 25 s pieces in the browser (lib/audioChunks.ts).
//
// G4 (Phase 1 quotation): inside a visit, the pieces are transcribed through
// /api/consultation-recordings, which keeps the RAW transcript with the visit
// as source material (the edits below never change it) and keeps the audio
// for the hospital's retention period, if it has one. Without a visit (the
// standalone Consultation module) it falls back to transcribe-only, as before.
import { useRef, useState } from 'react';
import { authFetch } from '../api';
import { useSegmentedRecorder } from '../hooks/useSegmentedRecorder';
import { transcribeSegments } from '../utils/sarvam';
import { getVoiceLanguage, voiceLanguageLabel } from '../lib/voiceLanguages';
import { splitAudioFile } from '../lib/audioChunks';
import {
  createRecording, sendPiece, sendOriginalAudio, completeRecording, failRecording, saveEnglishText,
} from '../features/consultation/recordingApi';
import { RecordingsList } from '../features/consultation/RecordingsList';
import { Button } from './ui/Button';
import { cn } from '@/lib/cn';

type Step = 'idle' | 'transcript' | 'translated' | 'masked';

export interface ConsultationVoicePanelProps {
  onUseText?: (text: string) => void;
  onClose: () => void;
  /** The visit's numeric id; when present the recording and its raw transcript are saved to the visit. */
  visitId?: number | null;
}

export default function ConsultationVoicePanel({ onUseText, onClose, visitId }: ConsultationVoicePanelProps) {
  // Previously always started at 'en-IN' regardless of what any other voice
  // control on the page had — now reads the same tenant-wide default every
  // other mic uses (see lib/voiceLanguages.ts), for consistency across the
  // whole app rather than just within this one panel's own dropdown.
  const [lang]                    = useState(getVoiceLanguage);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress]   = useState<{ done: number; total: number } | null>(null);
  const [step, setStep]           = useState<Step>('idle');
  const [transcript, setTranscript] = useState('');
  const [translated, setTranslated] = useState('');
  const [masked, setMasked]       = useState('');
  const [redactions, setRedactions] = useState<Record<string, number> | null>(null);
  const [error, setError]         = useState('');
  const [recordingId, setRecordingId] = useState<number | null>(null);
  const [retentionDays, setRetentionDays] = useState<number | null>(null);
  const [uploadName, setUploadName] = useState<string | null>(null);
  const [listVersion, setListVersion] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  const { recording, timeElapsed, segments, totalBytes, error: recError, start, stop, reset: resetRecorder } = useSegmentedRecorder();
  const recorded = !recording && segments.length > 0 && step === 'idle';

  const reset = () => {
    resetRecorder(); setStep('idle'); setTranscript(''); setTranslated(''); setMasked('');
    setRedactions(null); setError(''); setRecordingId(null); setRetentionDays(null); setUploadName(null); setProgress(null);
  };

  const showTranscript = (text: string) => {
    if (!text) {
      setError('No speech was detected in that recording. Try again — speak clearly, hold the mic closer, and record for at least a few seconds.');
      return;
    }
    setTranscript(text);
    setStep('transcript');
    if (lang === 'en-IN') { setTranslated(text); setStep('translated'); }
  };

  /** Transcribes pieces through the recordings API, in order; the server keeps the raw text. */
  const transcribeToVisit = async (
    source: 'recorded' | 'uploaded', pieces: { blob: Blob; name: string; start?: number; end?: number }[],
    durationSeconds: number, original?: File,
  ) => {
    const rec = await createRecording(visitId as number, source, lang, original?.name);
    setRecordingId(rec.id);
    setRetentionDays(rec.retention_days);
    try {
      if (original) await sendOriginalAudio(rec.id, original);
      setProgress({ done: 0, total: pieces.length });
      for (let i = 0; i < pieces.length; i++) {
        const p = pieces[i]!;
        await sendPiece(rec.id, i, p.blob, p.name, p.start, p.end);
        setProgress({ done: i + 1, total: pieces.length });
      }
      const { raw_text } = await completeRecording(rec.id, durationSeconds);
      setListVersion((v) => v + 1);
      return raw_text;
    } catch (e) {
      await failRecording(rec.id, e instanceof Error ? e.message : String(e)).catch(() => {});
      setListVersion((v) => v + 1);
      throw e;
    }
  };

  const doTranscribe = async () => {
    if (!segments.length) return;
    setProcessing(true); setError('');
    try {
      const text = visitId
        ? await transcribeToVisit('recorded', segments.map((blob, i) => ({ blob, name: `piece-${i + 1}.webm` })), timeElapsed)
        : await transcribeSegments(segments, lang, 'consultation-voice');
      showTranscript(text);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    setProcessing(false); setProgress(null);
  };

  const onFile = async (file: File | undefined) => {
    if (!file || !visitId) return;
    if (fileRef.current) fileRef.current.value = '';
    setProcessing(true); setError(''); setUploadName(file.name);
    try {
      const { pieces, duration } = await splitAudioFile(file);
      const text = await transcribeToVisit('uploaded',
        pieces.map((p, i) => ({ blob: p.blob, name: `piece-${i + 1}.wav`, start: p.start, end: p.end })), duration, file);
      showTranscript(text);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    setProcessing(false); setProgress(null);
  };

  const doTranslate = async () => {
    if (!transcript) return;
    setProcessing(true); setError('');
    try {
      const res  = await authFetch('/api/voice/translate', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: transcript, source_language_code: lang, target_language_code: 'en-IN' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Translation failed');
      setTranslated(data.translated_text || '');
      setStep('translated');
      if (recordingId) saveEnglishText(recordingId, data.translated_text || '').catch(() => {});
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    setProcessing(false);
  };

  const doMask = async () => {
    if (!translated) return;
    setProcessing(true); setError('');
    try {
      const res  = await authFetch('/api/ai/mask-pii', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: translated }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'PII masking failed');
      setMasked(data.masked || '');
      setRedactions(data.redactions || {});
      setStep('masked');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    setProcessing(false);
  };

  const formatTime = (s: number) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;
  const redactionSummary = redactions && Object.keys(redactions).length
    ? Object.entries(redactions).map(([k,v]) => `${k} ×${v}`).join(', ')
    : 'No PII patterns matched';
  const progressLabel = progress ? ` ${progress.done}/${progress.total}` : '';

  return (
    <div className="border-b border-line bg-muted/40 px-4 py-3">
      <div className="mb-2.5 flex items-center justify-between">
        <div className="text-[13px] font-bold text-ink">🎙 Consultation recording</div>
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>✕ Close</Button>
      </div>

      {step === 'idle' && !recorded && (
        <div className="flex flex-wrap items-center gap-2.5">
          {!recording ? (
            <Button type="button" size="md" variant="primary" onClick={start} disabled={processing}>🎙 Start recording</Button>
          ) : (
            <Button type="button" size="md" variant="danger" onClick={stop}>
              ⏹ Stop recording · {formatTime(timeElapsed)}
            </Button>
          )}
          {!recording && visitId && (
            <>
              <Button type="button" size="md" variant="ghost" onClick={() => fileRef.current?.click()} disabled={processing}>
                {processing && uploadName ? `⏳ Transcribing ${uploadName}…${progressLabel}` : '⬆ Upload audio file'}
              </Button>
              <input ref={fileRef} type="file" accept="audio/*,.mp3,.m4a,.wav,.webm,.ogg,.aac" className="hidden"
                onChange={(e) => onFile(e.target.files?.[0])} aria-label="Audio file to transcribe" />
            </>
          )}
          <span className="text-[11px] text-ink-muted">Manual control only — recording starts/stops only when you click. No length limit.</span>
        </div>
      )}

      {recorded && (
        <div>
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="text-[12px] text-ink-muted">Recording ready · {formatTime(timeElapsed)} · {(totalBytes/1024).toFixed(1)} KB</span>
            <Button type="button" size="sm" variant="ghost" onClick={reset} disabled={processing}>🔄 Re-record</Button>
            <Button type="button" size="sm" variant="primary" onClick={doTranscribe} disabled={processing}>
              {processing ? `⏳ Transcribing…${progressLabel}` : '🔍 Transcribe'}
            </Button>
          </div>
          {timeElapsed < 2 && (
            <div className="mt-1.5 text-[11px] text-warn-ink">
              ⚠ That recording is very short ({formatTime(timeElapsed)}) — speech recognition works best with at least a few seconds of clear audio.
            </div>
          )}
          {totalBytes > 0 && timeElapsed >= 2 && totalBytes < timeElapsed * 2000 && (
            <div className="mt-1.5 text-[11px] text-danger-ink">
              ⚠ Only {(totalBytes/1024).toFixed(1)} KB was captured for {formatTime(timeElapsed)} of recording — that's too little audio data for a clip this long. This usually means the wrong microphone is selected, or the mic is muted at the OS/browser level. Check your input device and try again.
            </div>
          )}
        </div>
      )}

      {(step === 'transcript' || step === 'translated' || step === 'masked') && (
        <div>
          {recordingId && (
            <div className="mb-2 rounded-[var(--radius)] bg-info-soft px-2.5 py-1.5 text-[11.5px] text-info-ink">
              The original transcript is saved with this visit as source material — editing it below does not change the saved copy.{' '}
              {retentionDays ? `The audio is kept for ${retentionDays} days.` : 'The audio is not kept (hospital setting).'}{' '}
              Speakers are not separated by the current speech service.
            </div>
          )}
          <div className="mb-2">
            <label className="lbl">Transcript ({voiceLanguageLabel(lang)})</label>
            <textarea className="h-16 resize-y text-[12px]" value={transcript} onChange={e=>setTranscript(e.target.value)} />
          </div>

          {lang !== 'en-IN' && step === 'transcript' && (
            <Button type="button" size="sm" variant="primary" className="mb-2" onClick={doTranslate} disabled={processing}>
              {processing ? '⏳ Translating…' : '🌐 Translate to English'}
            </Button>
          )}

          {(step === 'translated' || step === 'masked') && (
            <div className="mb-2">
              <label className="lbl">English translation (review before masking)</label>
              <textarea className="h-16 resize-y text-[12px]" value={translated} onChange={e=>setTranslated(e.target.value)} />
            </div>
          )}

          {step === 'translated' && (
            <Button type="button" size="sm" variant="primary" className="mb-2" onClick={doMask} disabled={processing}>
              {processing ? '⏳ Masking…' : '🔒 Mask PII & review'}
            </Button>
          )}

          {step === 'masked' && (
            <div>
              <label className="lbl">Masked text — this is what will be sent to the AI ({redactionSummary})</label>
              <textarea className={cn('h-20 resize-y border-success/40 bg-success-soft text-[12px]')} value={masked} onChange={e=>setMasked(e.target.value)} />
              <div className="mt-2 flex gap-2">
                <Button type="button" size="sm" variant="ghost" onClick={reset}>🔄 Start over</Button>
                <Button type="button" size="sm" variant="success" onClick={() => onUseText?.(masked)}>→ Use in consultation</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {(error || recError) && <div className="mt-2 rounded-[var(--radius)] border border-danger/30 bg-danger-soft px-2.5 py-1.5 text-[12px] text-danger-ink">{error || recError}</div>}

      {visitId && step === 'idle' && !recorded && <RecordingsList visitId={visitId} version={listVersion} />}
    </div>
  );
}
